import { defineStore } from 'pinia'
import {
  EnumerationsDocumentCenterTab,
  EnumerationsNotificationType,
  EnumerationsRole,
  EnumerationsServiceType,
} from 'client'
import { AxiosError } from 'axios'
import { useAuthStore } from './auth'
import { useAAPExportReportsStore } from './AAPExportReports'
import { useRootStore } from './root'
import { useSystemAlertsClientStore } from './systemAlertsClient'
import type { NotificationsNotificationDTO } from 'client'
import { HNRV2, switchToClient } from '~/helpers'
import { COPY, PATH } from '~/constants/strings'
import { notifyFailure } from '~/utils/notifications'
import { useUserClaims } from '~/composables/useUserClaims'
import { api } from '~/services'
import { useProjectReportsStore } from './projectReports'

interface INotificationStore {
  allNotifications: NotificationsNotificationDTO[]
  dropdownNotifications: NotificationsNotificationDTO[]
  isGetAllNotificationsLoading: boolean
  isMarkNotificationsAsReadLoading: boolean
}

const defaultStore: INotificationStore = {
  allNotifications: [],
  dropdownNotifications: [],
  isGetAllNotificationsLoading: true,
  isMarkNotificationsAsReadLoading: false,
}

export const useNotificationsStore = defineStore('notifications', {
  state: (): INotificationStore => defaultStore,
  getters: {
    unreadNotifications(): NotificationsNotificationDTO[] {
      return this.allNotifications.filter((notification) => !notification.read)
    },
    isNotificationPage(): boolean {
      const RootStore = useRootStore()
      return RootStore.currentPath === PATH.NOTIFICATIONS
    },
    notificationActionDeeplinkPath(): Record<
      string,
      (notification: NotificationsNotificationDTO) => string
    > {
      const AuthStore = useAuthStore()
      const { hasReadWriteDocumentsDeliverablesServiceFunctionPermission } =
        useUserClaims()

      const getServiceLineNameFromNotification = (
        notification: NotificationsNotificationDTO
      ) => {
        const serviceLineNameAssociatedWithNotification =
          AuthStore.user?.services
            .find(
              (service) => service.serviceType === notification.serviceTypeID
            )
            ?.name.toLowerCase()

        return serviceLineNameAssociatedWithNotification
      }

      const getDocumentCenterFileUploadedPath = (
        notification: NotificationsNotificationDTO
      ): string => {
        const tab =
          AuthStore.hasAccessToMultipleClients &&
          AuthStore.user &&
          [
            EnumerationsRole.BerkshireSystemAdmin,
            EnumerationsRole.BerkshireDataAdmin,
            EnumerationsRole.BerkshireCSLeadership,
            EnumerationsRole.BerkshireConsultant,
          ].includes(AuthStore.user.roleID)
            ? EnumerationsDocumentCenterTab.AllClients
            : EnumerationsDocumentCenterTab.CurrentClient

        const documentUploadID =
          notification.documentCenterNotification.documentUploadID
        return `${PATH.DOCUMENT_CENTER}?tab=${tab}&uploadID=${documentUploadID}`
      }

      const getManageServiceDeliverablePath = (
        notification: NotificationsNotificationDTO
      ): string => {
        const serviceLine =
          notification.serviceDeliverableNotification?.serviceLineName.toLowerCase()
        const pageID = notification.serviceDeliverableNotification?.pageID

        return `/${serviceLine}/service-deliverables/${pageID}${
          hasReadWriteDocumentsDeliverablesServiceFunctionPermission.value
            ? '/manage'
            : ''
        }`
      }

      return {
        [EnumerationsNotificationType.AccessExpiredClient]: () => {
          return PATH.CLIENT_SUPPORT_ACCESS
        },
        [EnumerationsNotificationType.AccessExpiredProductSupport]: () => {
          return PATH.ADMIN_SUPPORT_ACCESS
        },
        [EnumerationsNotificationType.FileAccessGranted]: (notification) => {
          return getDocumentCenterFileUploadedPath(notification)
        },
        [EnumerationsNotificationType.FileUploadedToGeneral]: (
          notification
        ) => {
          const documentUploadID =
            notification.documentCenterNotification.documentUploadID
          return `${PATH.DOCUMENT_CENTER}?tab=${COPY.ALL_CLIENTS}&uploadID=${documentUploadID}`
        },
        [EnumerationsNotificationType.FileUploadedToYou]: (notification) => {
          return getDocumentCenterFileUploadedPath(notification)
        },
        [EnumerationsNotificationType.NewPlanAssignmentPublish]: (
          notification
        ) => {
          const newPlanID = notification.newPlanAssignmentNotification.newPlanID
          const serviceLineName =
            getServiceLineNameFromNotification(notification)
          return `/${serviceLineName}/data-viz/overview?selectedPlanID=${newPlanID}`
        },
        [EnumerationsNotificationType.NewPlanAssignmentUpdateUser]: (
          notification
        ) => {
          const serviceLineName =
            getServiceLineNameFromNotification(notification)
          return `/${serviceLineName}/reports`
        },
        [EnumerationsNotificationType.NewServiceDeliverableAvailable]: (
          notification
        ) => {
          return getManageServiceDeliverablePath(notification)
        },
        [EnumerationsNotificationType.RequestedAccess]: () => {
          return PATH.CLIENT_SUPPORT_ACCESS
        },
        [EnumerationsNotificationType.ServiceDeliverablePageUpdated]: (
          notification
        ) => {
          return getManageServiceDeliverablePath(notification)
        },
        [EnumerationsNotificationType.SSOExpiring]: () => {
          return PATH.USER_PROFILE
        },
      }
    },
    getPathForNotification(): (
      notification: NotificationsNotificationDTO
    ) => string | undefined {
      return (notification) =>
        Object.hasOwn(
          this.notificationActionDeeplinkPath,
          notification.notificationTypeID
        )
          ? this.notificationActionDeeplinkPath[
              notification.notificationTypeID
            ](notification)
          : undefined
    },
    notificationActions(): Record<
      string,
      ((notification: NotificationsNotificationDTO) => any) | undefined
    > {
      const goToNotificationDeeplink = async (
        notification: NotificationsNotificationDTO
      ) => {
        const AuthStore = useAuthStore()
        const notificationPath = this.getPathForNotification(notification)

        if (!notificationPath) {
          notifyFailure('Problem opening notification link')
          return
        }

        await AuthStore.redirectToNewSubdomainIfNecessary({
          targetSubdomain: notification.subdomain,
          newPath: notificationPath,
          setOriginalPath: false,
        })
      }
      const handleExportCallToAction = async (
        notification: NotificationsNotificationDTO
      ) => {
        if (notification.serviceTypeID === EnumerationsServiceType.Compliance) {
          const AAPExportStore = useAAPExportReportsStore()
          await AAPExportStore.onDownloadExport(
            notification.exportProgressInfo.reportDownloadID
          )
        } else if (
          notification.serviceTypeID ===
          EnumerationsServiceType.WorkforceAnalytics
        ) {
          const ProjectReportsStore = useProjectReportsStore()
          await ProjectReportsStore.downloadReport(notification.notificationID)
        }
      }
      return {
        [EnumerationsNotificationType.AccessExpiredClient]: async (
          notification
        ) => {
          await goToNotificationDeeplink(notification)
        },
        [EnumerationsNotificationType.AccessExpiredProductSupport]: async (
          notification
        ) => {
          await goToNotificationDeeplink(notification)
        },
        [EnumerationsNotificationType.Export]: async (notification) => {
          await handleExportCallToAction(notification)
        },
        [EnumerationsNotificationType.FileAccessGranted]: async (
          notification
        ) => {
          await goToNotificationDeeplink(notification)
        },
        [EnumerationsNotificationType.FileUploadedToGeneral]: async (
          notification
        ) => {
          await goToNotificationDeeplink(notification)
        },

        [EnumerationsNotificationType.FileUploadedToYou]: async (
          notification
        ) => {
          await goToNotificationDeeplink(notification)
        },

        [EnumerationsNotificationType.NewClientAssignment]: ({
          newClientAssignmentNotification,
        }) => {
          switchToClient({
            clientID: newClientAssignmentNotification.newClientID,
            clientSubdomain: newClientAssignmentNotification?.subdomain,
          })
        },

        [EnumerationsNotificationType.NewPlanAssignmentPublish]: async (
          notification
        ) => {
          await goToNotificationDeeplink(notification)
        },

        [EnumerationsNotificationType.NewPlanAssignmentUpdateUser]: async (
          notification
        ) => {
          await goToNotificationDeeplink(notification)
        },

        [EnumerationsNotificationType.NewServiceDeliverableAvailable]: async (
          notification
        ) => {
          await goToNotificationDeeplink(notification)
        },
        [EnumerationsNotificationType.AAPSummaryExport]: async (
          notification
        ) => {
          await handleExportCallToAction(notification)
        },
        // push to support landing page
        [EnumerationsNotificationType.RequestedAccess]: async (
          notification
        ) => {
          await goToNotificationDeeplink(notification)
        },

        [EnumerationsNotificationType.ServiceDeliverablePageUpdated]: async (
          notification
        ) => {
          await goToNotificationDeeplink(notification)
        },
        [EnumerationsNotificationType.SystemAlert]: async ({
          systemAlertNotification,
        }) => {
          const SystemAlertsClientStore = useSystemAlertsClientStore()

          await SystemAlertsClientStore.getAlertDetailsForUser({
            alertId: systemAlertNotification.systemAlertID,
          })

          if (SystemAlertsClientStore.alertDetailsForUser) {
            const alertUnread =
              !SystemAlertsClientStore.alertDetailsForUser.acknowledgedDate

            if (alertUnread) {
              await SystemAlertsClientStore.handleSystemAlertReviewModal()
              const unreadAlerts = SystemAlertsClientStore.alerts.length > 0

              if (unreadAlerts) {
                return
              }
            }

            SystemAlertsClientStore.isSystemAlertViewModalOpen = true
            return
          }

          notifyFailure('Something went wrong getting alert details')
        },
        // * temporary, might be a page dedicated to SSO in the future.
        [EnumerationsNotificationType.SSOExpiring]: async (notification) => {
          await goToNotificationDeeplink(notification)
        },
        [EnumerationsNotificationType.DataExport]: async (notification) => {
          const AAPExportStore = useAAPExportReportsStore()
          await AAPExportStore.onDownloadExport(notification.notificationID)
        },
      }
    },
    exportNotificationTypes(): EnumerationsNotificationType[] {
      return [
        EnumerationsNotificationType.Export,
        EnumerationsNotificationType.AAPSummaryExport,
      ]
    },
  },
  actions: {
    async markAllNotificationsAsRead() {
      const response = await HNRV2({
        fn: () => api.notifications.apiNotificationsReadPut(),
      })
      if (response instanceof AxiosError) {
        notifyFailure('Something went wrong marking all notifications as read.')

        return
      }
      this.allNotifications = this.allNotifications.map((notification) => ({
        ...notification,
        read: true,
      }))
    },
    async markNotificationAsRead(notification: NotificationsNotificationDTO) {
      const response = await HNRV2({
        fn: () =>
          api.notifications.apiNotificationsNotificationIDReadPut(
            notification.notificationID
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure('Something went wrong marking the notification as read.')
        return
      }

      const readNotificationIndex = this.allNotifications.findIndex(
        (noti) => noti.notificationID === notification.notificationID
      )
      this.allNotifications[readNotificationIndex].read = true
    },
    async getNotifications(
      { page, rowsPerPage }: { page: number | null; rowsPerPage: number } = {
        page: null,
        rowsPerPage: 5,
      }
    ): Promise<NotificationsNotificationDTO[] | false> {
      const response = await HNRV2({
        fn: () =>
          api.notifications.apiNotificationsGet(page ?? undefined, rowsPerPage),
      })

      if (response instanceof AxiosError) {
        notifyFailure('Something went wrong retrieving notifications.')
        return false
      }

      return response.notifications
    },
    async getDropdownNotifications(): Promise<
      NotificationsNotificationDTO[] | void
    > {
      const notifications = await this.getNotifications()
      if (notifications) {
        // remove clientName from dropdown notifications
        this.dropdownNotifications = notifications.map((notification) => ({
          ...notification,
          clientName: notification?.systemAlertNotification?.systemAlertID
            ? ''
            : notification.clientName,
        }))
        return notifications
      }
    },
    async getPageNotifications(
      {
        page,
        rowsPerPage,
        signalR,
      }: { page: number | null; rowsPerPage: number; signalR?: boolean } = {
        page: 1,
        rowsPerPage: 15,
        signalR: false,
      }
    ): Promise<NotificationsNotificationDTO[] | void> {
      const notifications = await this.getNotifications({ page, rowsPerPage })
      if (notifications) {
        // removes clientName from notifications if it's a system alert
        const parsedNotifications = notifications.map((notification) => ({
          ...notification,
          clientName:
            notification.notificationTypeID ===
            EnumerationsNotificationType.SystemAlert
              ? ' '
              : notification.clientName,
        }))

        if (signalR) {
          const [newNotification] = parsedNotifications
          // append latest notification to the top of the list
          this.allNotifications.unshift(newNotification)
          return parsedNotifications
        }
        if (page && page > 1) {
          this.allNotifications = [
            ...this.allNotifications,
            ...parsedNotifications,
          ]
        } else {
          this.allNotifications = parsedNotifications
        }
        return parsedNotifications
      }
    },
    async viewNotifications() {
      const AuthStore = useAuthStore()
      if (!AuthStore.user || !AuthStore.user?.hasUnviewedNotifications) return

      const response = await HNRV2({
        fn: () => api.notifications.apiNotificationsViewedPut(),
      })

      if (response instanceof AxiosError) {
        return
      }

      AuthStore.user.hasUnviewedNotifications = false
    },
    async notificationAction(notification: NotificationsNotificationDTO) {
      if (this.isNotificationPage && !notification.read) {
        await this.markNotificationAsRead(notification)
      }

      const action = this.notificationActions[notification.notificationTypeID]
      if (action) {
        await action(notification)
      } else {
        console.warn(
          `No action defined for notification type: ${notification.notificationTypeID}`
        )
      }
    },
  },
})
